import { RefObject, useEffect, useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import { throttleWithTrailing } from '../helper-functions/throttle';
import { useAppSelector } from '../../state/store-hooks';
import { postMessage } from '../helper-functions/post-message';

interface IUsePostResizeProps {
  wrapRef: RefObject<HTMLDivElement>;
  containerRef: RefObject<HTMLDivElement>;
}

const usePostResizeEvent = ({ wrapRef, containerRef }: IUsePostResizeProps): void => {
  const store = useAppSelector(
    ({ initTheme, profileInfo }) => ({ initTheme, profileInfo }),
    shallowEqual,
  );

  const { cid, order_id } = useMemo(() => {
    const cid = (store?.profileInfo?.customer?.pin || store?.initTheme?.cid) ?? '';
    const order_id = (store?.profileInfo?.order_id || store?.initTheme?.order_id) ?? '';
    return { cid, order_id };
  }, [store?.profileInfo, store?.initTheme]);

  useEffect(() => {
    if (!containerRef?.current || !wrapRef?.current) return;

    const handleResize = throttleWithTrailing(() => {
      requestAnimationFrame(() => {
        const wrapStyle = window.getComputedStyle(wrapRef.current!);
        const wpt = parseFloat(wrapStyle.paddingTop) || 0;
        const wpb = parseFloat(wrapStyle.paddingBottom) || 0;
        const wmt = parseFloat(wrapStyle.marginTop) || 0;
        const wmb = parseFloat(wrapStyle.marginBottom) || 0;

        const height = containerRef.current.clientHeight + wpt + wpb + wmt + wmb;

        if (Number.isNaN(height) || !store.initTheme?.currentDomain) return;

        postMessage(
          {
            event_type: 'resize',
            height,
            cid,
            order_id,
          },
          store?.initTheme?.currentDomain,
        );
      });
    }, 200);

    const resizeObserver = new ResizeObserver(handleResize);

    resizeObserver.observe(containerRef.current);

    const mutationObserver = new MutationObserver(handleResize);

    mutationObserver.observe(containerRef.current, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    handleResize();

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [cid, containerRef, order_id, store.initTheme?.currentDomain, wrapRef]);
};

export default usePostResizeEvent;
